import { RefObject, useEffect, useRef } from 'react'

interface AnchorMessageData {
  type: string
  top?: number
}

// Marketing links deep-link to this hash to land users on the compare module, which lives
// partway down the nested iframe chain rather than in the top-level page's own DOM.
export const SCROLL_ANCHOR_HASH = '#iphone-comparison'

// Scrolls the top-level window to the compare module reported by the viewer iframe (see
// viewer.html's 'anchor' postMessage) whenever the page lands on/navigates to SCROLL_ANCHOR_HASH.
export const useScrollToAnchor = (iframeRef: RefObject<HTMLIFrameElement>, iframeSrc: string) => {
  const anchorTopRef = useRef<number | null>(null)
  const hasScrolledRef = useRef(false)

  useEffect(() => {
    const expectedOrigin = new URL(iframeSrc, window.location.href).origin

    // Returns whether the scroll actually happened — anchorTopRef may still be null if the
    // iframe (possibly still loading="lazy") hasn't reported a position yet.
    const scrollToAnchor = () => {
      const iframe = iframeRef.current
      if (!iframe || anchorTopRef.current === null) return false
      const target = iframe.getBoundingClientRect().top + window.scrollY + anchorTopRef.current
      window.scrollTo({ top: target })
      return true
    }

    const handleHashChange = () => {
      if (window.location.hash !== SCROLL_ANCHOR_HASH) {
        hasScrolledRef.current = false // re-arm for the next time the hash matches
        return
      }
      if (!hasScrolledRef.current) hasScrolledRef.current = scrollToAnchor()
    }

    const handleMessage = (event: MessageEvent<AnchorMessageData>) => {
      const iframe = iframeRef.current
      if (!iframe || event.source !== iframe.contentWindow || event.origin !== expectedOrigin) return
      if (event.data?.type !== 'anchor' || typeof event.data.top !== 'number') return

      anchorTopRef.current = event.data.top

      if (!hasScrolledRef.current && window.location.hash === SCROLL_ANCHOR_HASH) {
        hasScrolledRef.current = scrollToAnchor()
      }
    }

    window.addEventListener('message', handleMessage)
    window.addEventListener('hashchange', handleHashChange)
    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [iframeRef, iframeSrc])
}
